// Pure data-transform helpers shared between the interactive grid (GlideGrid)
// and the CSV export path (EventManagerPage's Download CSV button). No React,
// no CSS — safe to import from a bun:test file directly.

import type { GridColumnDef, SortState, FilterRule, FilterOperator } from './grid-columns';
import { operatorNeedsValue } from './grid-columns';

// ── Filter logic ─────────────────────────────────────────────────────────────

export function matchesFilter(rawVal: unknown, op: FilterOperator, filterValue: string): boolean {
  const primitive = typeof rawVal === 'string' || typeof rawVal === 'number' || typeof rawVal === 'boolean';
  const str = primitive ? String(rawVal) : '';
  const lower = str.toLowerCase();
  const fv = filterValue.toLowerCase();

  switch (op) {
    case 'equals': return lower === fv;
    case 'not_equal': return lower !== fv;
    case 'contains': return lower.includes(fv);
    case 'does_not_contain': return !lower.includes(fv);
    case 'is_empty': return rawVal == null || str === '';
    case 'is_not_empty': return rawVal != null && str !== '';
    case 'greater_than': return Number(rawVal) > Number(filterValue);
    case 'less_than': return Number(rawVal) < Number(filterValue);
    case 'is_true': return rawVal === true;
    case 'is_false': return rawVal !== true;
    default: return true;
  }
}

export function applyFilterRules<T extends Record<string, any>>(rows: T[], filters: FilterRule[]): T[] {
  // Only apply filters that are "complete" — skip filters with empty values
  // when the operator needs a value.
  const activeFilters = filters.filter(f => operatorNeedsValue(f.operator) === false || f.value.trim() !== '');
  if (activeFilters.length === 0) return rows;
  return rows.filter(row => activeFilters.every(f => matchesFilter(row[f.field], f.operator, f.value)));
}

// ── Sort logic ───────────────────────────────────────────────────────────────

export function sortRows<T extends Record<string, any>>(rows: T[], sort: SortState, columns: GridColumnDef[]): T[] {
  if (sort.dir === null || sort.colIndex < 0 || sort.colIndex >= columns.length) return rows;
  const field = columns[sort.colIndex].field;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

// ── Cell display resolution (text/CSV path only — Boolean/Bubble glide cell
//    kinds are handled separately by GlideGrid's getCellContent) ────────────

const MET_WITH_FIELDS = new Set(['primary_met_with', 'secondary_met_with', 'tertiary_met_with', 'follow_up_owner']);

export interface CellDisplayContext {
  tableMap: Map<number, string>;
  userNameMap: Map<string, string>;
}

export function cellDisplayValue(
  colDef: GridColumnDef,
  row: Record<string, any>,
  ctx: CellDisplayContext,
): string {
  const rawVal = row[colDef.field];

  if (colDef.formatFn) {
    return colDef.formatFn(rawVal, row);
  }
  if (colDef.field === 'table_id') {
    const tid = rawVal as number | null | undefined;
    return tid != null ? (ctx.tableMap.get(tid) ?? `#${tid}`) : '';
  }
  if (MET_WITH_FIELDS.has(colDef.field)) {
    const userId = rawVal as string | null | undefined;
    return userId ? (ctx.userNameMap.get(userId) ?? userId) : '';
  }
  if (colDef.field === 'role' || colDef.field === 'attending_status') {
    return rawVal ? String(rawVal).charAt(0).toUpperCase() + String(rawVal).slice(1) : '';
  }
  if (typeof rawVal === 'boolean') {
    return rawVal === true ? 'true' : 'false';
  }
  return rawVal == null ? '' : String(rawVal);
}

// ── CSV building ─────────────────────────────────────────────────────────────

function escapeCsv(val: string): string {
  return `"${val.replace(/"/g, '""')}"`;
}

export function buildCsv(
  rows: Record<string, any>[],
  columns: GridColumnDef[],
  ctx: CellDisplayContext,
): string {
  const header = columns.map(c => escapeCsv(c.title)).join(',');
  const csvRows = rows.map(row =>
    columns.map(c => escapeCsv(cellDisplayValue(c, row, ctx))).join(','),
  );
  return [header, ...csvRows].join('\n');
}

// ── Filename ─────────────────────────────────────────────────────────────────

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9]/gi, '_');
}

export function exportFilename(campaignName: string | null | undefined, viewSlug: string): string {
  const name = campaignName && campaignName.trim() !== '' ? campaignName : 'campaign';
  const date = new Date().toISOString().slice(0, 10);
  return `${sanitize(name)}_${sanitize(viewSlug)}_${date}.csv`;
}
